import {
  DropletIcon,
  FlameIcon,
  CloudLightningIcon,
  BugIcon,
  SearchIcon,
  WindIcon,
  ChefHatIcon,
  BathIcon,
  LayersIcon,
  SquareStackIcon,
  PaintbrushIcon,
  HomeIcon,
  HammerIcon,
  FileCheckIcon,
} from 'lucide-react'

export const COMPANY = {
  name: 'Dream Shield',
  fullName: 'Dream Shield Restoration & Construction',
  phoneDisplay: '+1 (645) 216-5704',
  phoneHref: 'tel:+16452165704',
  location: 'Houston, Texas',
  serviceArea: 'Houston & Surrounding Areas',
  logo: 'https://cdn.magicpatterns.com/uploads/x3i7S87wc4vEm85NFJcfTp/706631750_122114456061283996_4547381391709479422_n.jpg',
  owner:
    'https://cdn.magicpatterns.com/uploads/3Dghma5vTa1pZFFbzFhCab/706377970_122114457939283996_3549600059827950164_n.jpg',
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/bca4e469-24c9-4665-a245-07124cb0ea55.jpg',
}

export type Service = {
  slug: string
  title: string
  short: string
  description: string
  icon: any
  accent: 'blue' | 'fire' | 'green'
  emergency?: boolean
}

export const SERVICES: Service[] = [
  {
    slug: 'water-damage-restoration',
    title: 'Water Damage Restoration',
    short: '24/7 emergency water extraction & drying.',
    description:
      'Rapid water extraction, structural drying, and full restoration — available around the clock to stop damage before it spreads.',
    icon: DropletIcon,
    accent: 'blue',
    emergency: true,
  },
  {
    slug: 'fire-damage-restoration',
    title: 'Fire Damage Restoration',
    short: 'Soot, smoke & structural fire recovery.',
    description:
      'Complete fire and smoke damage recovery — from soot removal and odor neutralization to rebuilding what was lost.',
    icon: FlameIcon,
    accent: 'fire',
  },
  {
    slug: 'storm-damage-repair',
    title: 'Storm Damage Repair',
    short: 'Roofing, siding & structural storm repair.',
    description:
      'Fast response to storm and hurricane damage across the Houston area — securing, drying, and rebuilding your home.',
    icon: CloudLightningIcon,
    accent: 'blue',
  },
  {
    slug: 'mold-remediation',
    title: 'Mold Remediation',
    short: 'Safe, certified mold removal.',
    description:
      "Certified mold inspection, containment, and remediation that protects your family's health and your home's integrity.",
    icon: BugIcon,
    accent: 'green',
  },
  {
    slug: 'leak-detection',
    title: 'Leak Detection',
    short: 'Precision non-invasive leak finding.',
    description:
      'Advanced, non-invasive leak detection technology to pinpoint hidden moisture before it becomes costly damage.',
    icon: SearchIcon,
    accent: 'blue',
  },
  {
    slug: 'structural-drying',
    title: 'Structural Drying',
    short: 'Industrial dehumidification & drying.',
    description:
      'Industrial-grade drying and dehumidification to fully restore structural moisture levels and prevent secondary damage.',
    icon: WindIcon,
    accent: 'blue',
  },
  {
    slug: 'kitchen-remodeling',
    title: 'Kitchen Remodeling',
    short: 'Luxury custom kitchen transformations.',
    description:
      'Bespoke kitchen design and construction — from layout and cabinetry to premium countertops and lighting.',
    icon: ChefHatIcon,
    accent: 'blue',
  },
  {
    slug: 'bathroom-remodeling',
    title: 'Bathroom Remodeling',
    short: 'Spa-inspired bathroom builds.',
    description:
      'Spa-inspired bathroom remodels featuring elegant tile, modern fixtures, and flawless waterproofing.',
    icon: BathIcon,
    accent: 'blue',
  },
  {
    slug: 'luxury-flooring',
    title: 'Luxury Flooring',
    short: 'Hardwood, tile & premium surfaces.',
    description:
      'Premium hardwood, tile, and engineered flooring installation with impeccable craftsmanship.',
    icon: LayersIcon,
    accent: 'blue',
  },
  {
    slug: 'drywall-installation-repair',
    title: 'Drywall Installation & Repair',
    short: 'Seamless drywall & finishing.',
    description:
      'Seamless drywall installation, patching, and finishing that leaves walls flawlessly smooth.',
    icon: SquareStackIcon,
    accent: 'blue',
  },
  {
    slug: 'interior-exterior-painting',
    title: 'Interior & Exterior Painting',
    short: 'Flawless premium finishes.',
    description:
      'Meticulous interior and exterior painting using premium, long-lasting finishes for a showroom result.',
    icon: PaintbrushIcon,
    accent: 'blue',
  },
  {
    slug: 'complete-home-remodeling',
    title: 'Complete Home Remodeling',
    short: 'Full-home luxury renovations.',
    description:
      'End-to-end home renovations that reimagine your space with luxury detail and structural excellence.',
    icon: HomeIcon,
    accent: 'blue',
  },
  {
    slug: 'full-reconstruction',
    title: 'Full Reconstruction',
    short: 'Rebuild after major damage.',
    description:
      'Ground-up reconstruction after catastrophic damage — rebuilding your home stronger and more beautiful than before.',
    icon: HammerIcon,
    accent: 'blue',
  },
  {
    slug: 'insurance-claim-assistance',
    title: 'Insurance Claim Assistance',
    short: 'We handle the claim for you.',
    description:
      'We work directly with your insurance carrier to document, file, and maximize your claim — start to finish.',
    icon: FileCheckIcon,
    accent: 'green',
  },
]

export const SERVICE_AREAS = [
  'Houston',
  'Sugar Land',
  'Katy',
  'Pearland',
  'Pasadena',
  'Cypress',
  'Spring',
  'Missouri City',
  'The Woodlands',
]

export const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Services', to: '/services' },
  { label: 'Projects', to: '/projects' },
  { label: 'Emergency', to: '/emergency' },
  { label: 'Insurance', to: '/insurance' },
  { label: 'Service Areas', to: '/service-areas' },
  { label: 'Reviews', to: '/reviews' },
  { label: 'Blog', to: '/blog' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Contact', to: '/contact' },
]

export const PROJECTS = [
  {
    title: 'Riverside Water Restoration',
    category: 'Water Damage',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/f2b0b01b-f318-49d2-bd9e-bdf6b5864c15.jpg',
  },
  {
    title: "Modern Chef's Kitchen",
    category: 'Kitchen Remodel',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/7d2ac33e-dff5-4f57-8f19-41ddd1e6c175.jpg',
  },
  {
    title: 'Spa Master Bath',
    category: 'Bathroom Remodel',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/a2af2433-2969-4e8c-b9e5-dab30d133b95.jpg',
  },
  {
    title: 'Wide-Plank Oak Floors',
    category: 'Luxury Flooring',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e35957ea-d358-4acf-a038-d11b88c89f73.jpg',
  },
]

export const TESTIMONIALS = [
  {
    name: 'Sarah Mitchell',
    location: 'Sugar Land, TX',
    quote:
      'After a burst pipe flooded our first floor, Dream Shield had a crew out within the hour. They handled everything — including our insurance claim. The restoration is flawless.',
    rating: 5,
  },
  {
    name: 'James Carter',
    location: 'Katy, TX',
    quote:
      'The team turned our fire-damaged kitchen into something better than new. Professional, transparent, and genuinely caring throughout the entire rebuild.',
    rating: 5,
  },
  {
    name: 'Elena Rodriguez',
    location: 'The Woodlands, TX',
    quote:
      'From mold remediation to a full bathroom remodel, the craftsmanship is outstanding. It truly feels like a luxury experience from start to finish.',
    rating: 5,
  },
  {
    name: 'Marcus Bell',
    location: 'Pearland, TX',
    quote:
      'Storm ripped through our roof and Dream Shield rebuilt it stronger than before. They protected our home and our family. Cannot recommend them enough.',
    rating: 5,
  },
]

export const PROCESS_STEPS = [
  {
    title: 'Emergency Response',
    text: 'Call us 24/7. We dispatch a certified crew fast to assess and secure your property.',
  },
  {
    title: 'Inspection & Plan',
    text: 'We document every detail, scope the damage, and build a transparent restoration plan.',
  },
  {
    title: 'Mitigation & Drying',
    text: 'Water extraction, structural drying, and containment to stop damage in its tracks.',
  },
  {
    title: 'Restoration & Rebuild',
    text: 'Expert craftsmanship rebuilds and remodels your home to a better-than-before finish.',
  },
  {
    title: 'Insurance & Handoff',
    text: 'We finalize your claim and walk you through every detail of your restored home.',
  },
]

export const FAQS = [
  {
    q: 'How fast can you respond to an emergency?',
    a: 'We offer true 24/7 emergency response across Houston and surrounding areas, with crews typically on-site within 60 minutes for water and fire emergencies.',
  },
  {
    q: 'Do you work directly with insurance companies?',
    a: 'Yes. We handle the entire claims process — documentation, filing, and direct communication with your carrier — to make restoration stress-free.',
  },
  {
    q: 'Are you licensed and certified?',
    a: 'Absolutely. Dream Shield is fully licensed, insured, and IICRC-certified for water, fire, and mold restoration.',
  },
  {
    q: 'What areas do you serve?',
    a: 'We serve Houston and all surrounding communities including Sugar Land, Katy, Pearland, Cypress, Spring, The Woodlands, and more.',
  },
  {
    q: 'Do you handle both restoration and remodeling?',
    a: 'Yes — we are a full-service restoration and construction company, from emergency mitigation to luxury remodels and full reconstruction.',
  },
]
